import { type MySubscription } from '@platform/contracts';
import {
  Button,
  Callout,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  IconBadge,
  Progress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@platform/ui';
import {
  Activity,
  AlertTriangle,
  Building2,
  CalendarRange,
  CreditCard,
  Download,
  FileText,
  Layers,
  Paperclip,
  Receipt,
  Wallet,
} from 'lucide-react';

import { Guard, PageHeader } from '../components/page';
import { InvoiceBadge, SubStatusBadge } from '../components/status-badges';
import { Loadable } from '../components/states';
import { fmtNum, useScopedQuery } from '../lib/console';
import { fmtDate, fmtMoney, fmtPeriod } from '../lib/format';
import { limitLines } from '../lib/limits';

const PERIOD_FMT = new Intl.DateTimeFormat('es-ES', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

function ClientSubscription({ s }: { s: MySubscription }) {
  const sub = s.subscription;
  const status = sub?.status;
  return (
    <div className="flex flex-col gap-5">
      {status === 'past_due' ? (
        <Callout tone="warning" icon={<AlertTriangle />} title="Tienes una factura vencida">
          Todo sigue funcionando. Si el pago no se registra, pasados {sub?.graceDays} días se dejan
          de buscar, analizar y contactar tiendas nuevas (las conversaciones en curso nunca se
          cortan).
        </Callout>
      ) : null}
      {status === 'suspended' ? (
        <Callout tone="destructive" icon={<AlertTriangle />} title="Suscripción suspendida">
          No entran leads nuevos ni se envían campañas nuevas. Las conversaciones en curso, el panel
          y el historial siguen disponibles. Se reactiva al registrar el pago.
        </Callout>
      ) : null}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex-row items-start gap-3">
            <IconBadge tone="cyan" size="lg">
              <Layers />
            </IconBadge>
            <div className="flex min-w-0 flex-col gap-1">
              <CardDescription>Plan</CardDescription>
              <CardTitle className="font-display text-lg">
                {s.plan?.name ?? 'Sin plan asignado'}
              </CardTitle>
              {s.plan && sub ? (
                <p className="text-sm text-muted-foreground">
                  {fmtMoney(sub.customPriceCents ?? s.plan.priceCents, s.plan.currency)} /{' '}
                  {s.plan.interval === 'month' ? 'mes' : 'año'}
                  {sub.customPriceCents !== null ? ' (precio pactado)' : ''}
                </p>
              ) : null}
            </div>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="flex-row items-start gap-3">
            <IconBadge tone="green" size="lg">
              <CalendarRange />
            </IconBadge>
            <div className="flex min-w-0 flex-col gap-1.5">
              <CardDescription>Estado</CardDescription>
              <div>{status ? <SubStatusBadge status={status} /> : '—'}</div>
              {sub ? (
                <p className="text-sm text-muted-foreground">
                  Período {fmtPeriod(sub.currentPeriodStart, sub.currentPeriodEnd)}
                </p>
              ) : null}
            </div>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="flex-row items-start gap-3">
            <IconBadge tone="navy" size="lg">
              <Building2 />
            </IconBadge>
            <div className="flex min-w-0 flex-col gap-1">
              <CardDescription>Cuenta</CardDescription>
              <CardTitle className="font-display text-lg">
                {s.counts.companies} {s.counts.companies === 1 ? 'empresa' : 'empresas'} ·{' '}
                {s.counts.users} {s.counts.users === 1 ? 'usuario' : 'usuarios'}
              </CardTitle>
            </div>
          </CardHeader>
        </Card>
      </div>
      <Card>
        <CardHeader>
          <CardTitle icon={<Activity />}>
            Uso de {PERIOD_FMT.format(new Date(`${s.period}-01T00:00:00Z`))}
          </CardTitle>
          <CardDescription>Se actualiza cada hora. Aviso al 80 % del límite.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {s.usage.map((u) => {
            const ratio = u.limit ? u.value / u.limit : null;
            return (
              <div key={u.metric} className="flex flex-col gap-1.5">
                <div className="flex justify-between text-sm">
                  <span>{u.label}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {fmtNum(u.value)} {u.limit === null ? '(sin límite)' : `/ ${fmtNum(u.limit)}`}
                  </span>
                </div>
                <Progress
                  label={u.label}
                  value={ratio}
                  tone={
                    u.level === 'hard' || u.level === 'grace'
                      ? 'destructive'
                      : u.level === 'ok'
                        ? 'default'
                        : 'warning'
                  }
                />
                {u.byTenant.length > 1 ? (
                  <p className="text-xs text-muted-foreground">
                    {u.byTenant.map((b) => `${b.tenantName}: ${fmtNum(b.value)}`).join(' · ')}
                  </p>
                ) : null}
              </div>
            );
          })}
          {limitLines(s.limits).length > 0 ? (
            <div className="border-t border-dashed pt-4">
              <p className="mb-2 text-sm font-medium">Tu plan incluye</p>
              <ul className="flex flex-wrap gap-2" aria-label="Límites del plan">
                {limitLines(s.limits).map((l) => (
                  <li
                    key={l.key}
                    className="inline-flex items-center gap-1.5 rounded-full border bg-muted/40 px-2.5 py-1 text-xs"
                  >
                    <l.icon className="size-3.5 text-highlight" aria-hidden />
                    {l.text}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {s.entitlements.reasons.length > 0 &&
          (!s.entitlements.canIngest || !s.entitlements.canPush) ? (
            <Callout tone="warning" icon={<AlertTriangle />} title="Hay etapas frenadas">
              {s.entitlements.reasons.join(' · ')}
            </Callout>
          ) : null}
        </CardContent>
      </Card>
      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle icon={<FileText />}>Facturas</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          {s.invoices.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">Todavía no hay facturas emitidas.</p>
          ) : (
            <div className="border-t [&>div]:rounded-none [&>div]:border-0 [&>div]:shadow-none">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="hidden sm:table-cell">Emitida</TableHead>
                    <TableHead className="hidden sm:table-cell">Vence</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="w-24" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {s.invoices.map((i) => (
                    <TableRow key={i.id}>
                      <TableCell className="font-medium">
                        <span className="inline-flex items-center gap-2">
                          <FileText className="size-4 text-muted-foreground" aria-hidden />
                          {i.number}
                        </span>
                      </TableCell>
                      <TableCell>
                        <InvoiceBadge status={i.status} />
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">{fmtDate(i.issuedAt)}</TableCell>
                      <TableCell className="hidden sm:table-cell">{fmtDate(i.dueDate)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtMoney(i.totalCents, i.currency)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button asChild size="sm" variant="ghost">
                          <a
                            href={`/api/billing/invoices/${i.id}/pdf`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Download /> PDF
                          </a>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle icon={<Wallet />}>Pagos registrados</CardTitle>
          </CardHeader>
          <CardContent>
            {s.payments.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin pagos registrados.</p>
            ) : (
              <ul className="flex flex-col divide-y text-sm">
                {s.payments.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5">
                    <span className="font-display font-semibold tabular-nums">
                      {fmtMoney(p.amountCents, p.currency)}
                    </span>
                    <span className="text-muted-foreground">{fmtDate(p.receivedAt)}</span>
                    {p.reference ? (
                      <span className="text-xs text-muted-foreground">{p.reference}</span>
                    ) : null}
                    {p.hasProof ? (
                      <a
                        className="ml-auto inline-flex items-center gap-1 text-highlight hover:underline"
                        href={`/api/billing/payments/${p.id}/proof`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Paperclip className="size-3.5" aria-hidden /> comprobante
                      </a>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle icon={<CreditCard />}>Cómo pagar</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-line">
              {s.paymentInstructions ?? 'La agencia te va a indicar los datos de pago.'}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function SubscriptionPage() {
  const q = useScopedQuery<MySubscription[]>(['billing-me'], '/api/billing/me', {
    requires: 'billing:read',
  });
  return (
    <Guard requires="billing:read">
      <PageHeader
        title="Plan y facturas"
        description="Tu plan, lo que llevas usado este mes, tus facturas y pagos."
      />
      <Loadable isLoading={q.isLoading} error={q.error} onRetry={() => void q.refetch()}>
        {q.data?.length === 0 ? (
          <EmptyState icon={<Receipt />} title="Sin suscripción en este alcance" />
        ) : (
          <div className="flex flex-col gap-10">
            {q.data?.map((s) => (
              <section key={s.clientId} aria-label={s.clientName}>
                {q.data.length > 1 ? (
                  <h2 className="mb-3 text-lg font-semibold">{s.clientName}</h2>
                ) : null}
                <ClientSubscription s={s} />
              </section>
            ))}
          </div>
        )}
      </Loadable>
    </Guard>
  );
}
